/**
 * Tokenizer du sous-ensemble C#/Unity accepté par le simulateur.
 * Aucune dépendance, aucun accès DOM. Lève `SimCompileError` (kind `syntax`)
 * sur un caractère inconnu, une chaîne non terminée ou un commentaire non fermé.
 */

import { KEYWORDS, PUNCTUATION, Token } from './tokens';
import { SimCompileError, syntaxError, unsupportedError } from './errors';

const isDigit = (c: string): boolean => c >= '0' && c <= '9';
const isIdentStart = (c: string): boolean => /[A-Za-z_À-ɏ]/.test(c);
const isIdentPart = (c: string): boolean => isIdentStart(c) || isDigit(c);

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  const src = source;
  let i = 0;
  let line = 1;
  let column = 1;

  const advance = (n = 1): void => {
    for (let k = 0; k < n; k++) {
      if (src[i] === '\n') {
        line++;
        column = 1;
      } else {
        column++;
      }
      i++;
    }
  };

  while (i < src.length) {
    const c = src[i];

    // Blancs
    if (c === ' ' || c === '\t' || c === '\r' || c === '\n' || c === ' ') {
      advance();
      continue;
    }

    // Commentaires
    if (c === '/' && src[i + 1] === '/') {
      while (i < src.length && src[i] !== '\n') advance();
      continue;
    }
    if (c === '/' && src[i + 1] === '*') {
      const startLine = line;
      const startCol = column;
      advance(2);
      let closed = false;
      while (i < src.length) {
        if (src[i] === '*' && src[i + 1] === '/') {
          advance(2);
          closed = true;
          break;
        }
        advance();
      }
      if (!closed) {
        throw new SimCompileError(
          syntaxError('Commentaire `/*` jamais fermé : il manque `*/` avant la fin du fichier.', startLine, startCol, { csharpCode: 'CS1035' })
        );
      }
      continue;
    }

    // Directives préprocesseur (#region, #if…) → hors périmètre
    if (c === '#') {
      throw new SimCompileError(
        unsupportedError('#' + readWord(src, i + 1), 'Les directives de préprocesseur (`#region`, `#if`…) ne sont pas au programme de cet exercice.', line, column)
      );
    }

    // Chaînes
    if (c === '"') {
      const startLine = line;
      const startCol = column;
      advance();
      let value = '';
      let closed = false;
      while (i < src.length) {
        const ch = src[i];
        if (ch === '\n') break;
        if (ch === '\\') {
          const next = src[i + 1];
          const map: Record<string, string> = { n: '\n', t: '\t', '"': '"', '\\': '\\', r: '\r', '0': '\0', "'": "'" };
          if (next !== undefined && next in map) {
            value += map[next];
            advance(2);
            continue;
          }
          throw new SimCompileError(syntaxError(`Séquence d'échappement inconnue \`\\${next ?? ''}\` dans la chaîne.`, line, column, { csharpCode: 'CS1009' }));
        }
        if (ch === '"') {
          advance();
          closed = true;
          break;
        }
        value += ch;
        advance();
      }
      if (!closed) {
        throw new SimCompileError(syntaxError('Chaîne de caractères jamais fermée : il manque un `"`.', startLine, startCol, { csharpCode: 'CS1010' }));
      }
      tokens.push({ kind: 'string', value, line: startLine, column: startCol });
      continue;
    }

    // Caractères 'x' → hors périmètre (type char)
    if (c === "'") {
      throw new SimCompileError(
        unsupportedError("'…' (char)", "Le type `char` et les littéraux entre apostrophes ne sont pas au programme ici.", line, column, 'Pour une touche du clavier, utilise `Input.GetKey(KeyCode.W)`.')
      );
    }

    // Nombres (y compris `.5f`)
    if (isDigit(c) || (c === '.' && isDigit(src[i + 1] ?? ''))) {
      const startLine = line;
      const startCol = column;
      let j = i;
      let hasDot = false;
      let hasExp = false;
      if (src[j] === '0' && (src[j + 1] === 'x' || src[j + 1] === 'X')) {
        j += 2;
        while (j < src.length && /[0-9a-fA-F]/.test(src[j])) j++;
        const raw = src.slice(i, j);
        advance(j - i);
        tokens.push({ kind: 'number', value: raw, line: startLine, column: startCol, numberKind: 'int', numberValue: parseInt(raw, 16) });
        continue;
      }
      while (j < src.length && isDigit(src[j])) j++;
      if (src[j] === '.' && isDigit(src[j + 1] ?? '')) {
        hasDot = true;
        j++;
        while (j < src.length && isDigit(src[j])) j++;
      }
      if (src[j] === 'e' || src[j] === 'E') {
        const k = src[j + 1] === '+' || src[j + 1] === '-' ? j + 2 : j + 1;
        if (isDigit(src[k] ?? '')) {
          hasExp = true;
          j = k;
          while (j < src.length && isDigit(src[j])) j++;
        }
      }
      const body = src.slice(i, j);
      let numberKind: 'int' | 'float' | 'double';
      const suffix = src[j];
      if (suffix === 'f' || suffix === 'F') {
        numberKind = 'float';
        j++;
      } else if (suffix === 'd' || suffix === 'D') {
        numberKind = 'double';
        j++;
      } else if (suffix === 'm' || suffix === 'M') {
        throw new SimCompileError(unsupportedError(body + suffix, 'Le type `decimal` (suffixe `m`) n\'est pas au programme ici.', startLine, startCol, 'Utilise un `float` : `' + body + 'f`.'));
      } else if (suffix === 'L' || suffix === 'l' || suffix === 'u' || suffix === 'U') {
        numberKind = 'int';
        j++;
      } else {
        numberKind = hasDot || hasExp ? 'double' : 'int';
      }
      if (isIdentPart(src[j] ?? '')) {
        throw new SimCompileError(syntaxError(`Nombre mal formé : \`${src.slice(i, j + 1)}\`.`, startLine, startCol));
      }
      const raw = src.slice(i, j);
      advance(j - i);
      tokens.push({ kind: 'number', value: raw, line: startLine, column: startCol, numberKind, numberValue: Number(body) });
      continue;
    }

    // Identifiants / mots-clés
    if (isIdentStart(c)) {
      const startLine = line;
      const startCol = column;
      let j = i;
      while (j < src.length && isIdentPart(src[j])) j++;
      const word = src.slice(i, j);
      advance(j - i);
      tokens.push({ kind: 'word', value: word, line: startLine, column: startCol });
      continue;
    }

    // Ponctuation
    let matched = false;
    for (const p of PUNCTUATION) {
      if (src.startsWith(p, i)) {
        tokens.push({ kind: 'punct', value: p, line, column });
        advance(p.length);
        matched = true;
        break;
      }
    }
    if (matched) continue;

    throw new SimCompileError(syntaxError(`Caractère inattendu \`${c}\`.`, line, column, { csharpCode: 'CS1056' }));
  }

  tokens.push({ kind: 'eof', value: '<fin du fichier>', line, column });
  return tokens;
}

function readWord(src: string, from: number): string {
  let j = from;
  while (j < src.length && isIdentPart(src[j])) j++;
  return src.slice(from, j);
}

export const isKeyword = (value: string): boolean => KEYWORDS.has(value);
